import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { TrainingService } from '@/services/training.service';
import { DocumentService } from '@/services/document.service';
import { DocumentType, DocumentStatus } from '@/models/document.model';
import { HIPAA_TRAINING_MODULES, COMPANY_TRAINING_MODULES } from '@/data/training-modules';
import { POLICY_DOCUMENTS } from '@/data/document-content';

/**
 * Comprehensive seeding page to add all training courses and documents from the zip file
 * This will create:
 * - 6 training courses (3 HIPAA levels + 3 Company levels)
 * - 18+ policy documents
 */
export default function SeedTrainingAndDocuments() {
  const { profile } = useAuth();
  const [seeding, setSeeding] = useState(false);
  const [seedResult, setSeedResult] = useState<{
    success: boolean;
    coursesCreated: number;
    documentsCreated: number;
    errors: string[];
  } | null>(null);

  const handleSeedAll = async () => {
    if (!profile) {
      alert('Please log in to seed data');
      return;
    }

    setSeeding(true);
    setSeedResult(null);
    const errors: string[] = [];
    let coursesCreated = 0;
    let documentsCreated = 0;

    try {
      // Seed Training Courses
      for (const module of [...HIPAA_TRAINING_MODULES, ...COMPANY_TRAINING_MODULES]) {
        try {
          // Check if course already exists by title
          const existingCourses = await TrainingService.getAllCourses();
          const exists = existingCourses.some(c => c.title === module.title);
          
          if (!exists) {
            await TrainingService.createCourse({
              ...module,
              createdBy: profile.id || 'system',
              // Store content as HTML string (will be rendered as HTML)
              content: typeof module.content === 'string' 
                ? module.content 
                : JSON.stringify(module.content || ''),
            });
            coursesCreated++;
          }
        } catch (error) {
          errors.push(`Failed to create course "${module.title}": ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      // Seed Documents - store content in Firestore metadata
      // In production, you may want to upload files to Firebase Storage
      for (const doc of POLICY_DOCUMENTS) {
        try {
          // Check if document already exists by name/title
          const existingDocs = await DocumentService.getAllDocuments();
          const exists = existingDocs.some(d => d.name === doc.title || d.name === doc.id);
          
          if (!exists) {
            // Store document content in Firestore metadata
            // For production, consider uploading to Firebase Storage
            await DocumentService.createDocument({
              name: doc.title,
              description: doc.description,
              type: mapCategoryToDocumentType(doc.category),
              status: DocumentStatus.active,
              fileUrl: '', // Will be populated if uploaded to Storage
              fileName: `${doc.id}_v${doc.version}.txt`,
              fileSize: new Blob([doc.content]).size,
              mimeType: 'text/plain',
              category: doc.category,
              tags: [doc.category, 'policy', 'seed', doc.id],
              ownerId: profile.id || 'system',
              ownerName: profile.fullName || profile.email || 'System',
              createdBy: profile.id || 'system',
              createdByName: profile.fullName || profile.email || 'System',
              version: parseFloat(doc.version),
              isLatestVersion: true,
              isPublic: false,
              allowedRoles: ['operationsAdmin', 'executive', 'hr', 'compliance', 'finance'],
              // Store full content in metadata for easy access
              metadata: {
                originalId: doc.id,
                originalVersion: doc.version,
                lastUpdated: doc.lastUpdated,
                pages: doc.pages,
                fullContent: doc.content, // Store full text content here
              },
            });
            documentsCreated++;
          }
        } catch (error) {
          errors.push(`Failed to create document "${doc.title}": ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
      }

      setSeedResult({
        success: errors.length === 0,
        coursesCreated,
        documentsCreated,
        errors,
      });
    } catch (error) {
      setSeedResult({
        success: false,
        coursesCreated,
        documentsCreated,
        errors: [`Failed to seed: ${error instanceof Error ? error.message : 'Unknown error'}`],
      });
    } finally {
      setSeeding(false);
    }
  };

  const mapCategoryToDocumentType = (category: string): DocumentType => {
    switch (category) {
      case 'hipaa':
      case 'compliance':
        return DocumentType.complianceDocument;
      case 'security':
        return DocumentType.policy;
      case 'hr':
        return DocumentType.employeeDocument;
      case 'operations':
        return DocumentType.policy;
      default:
        return DocumentType.other;
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Seed Training & Documents</h1>
        <p className="mt-2 text-gray-600">
          Import training courses and policy documents from the zip file
        </p>
      </div>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">What Will Be Created</h2>
        
        <div className="space-y-4 mb-6">
          <div>
            <h3 className="font-medium text-gray-900 mb-2">Training Courses ({HIPAA_TRAINING_MODULES.length + COMPANY_TRAINING_MODULES.length} total)</h3>
            <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
              <li>{HIPAA_TRAINING_MODULES.length} HIPAA Training Modules</li>
              <li>  - Executive Level (45 min)</li>
              <li>  - Management Level (60 min)</li>
              <li>  - Staff Level (45 min)</li>
              <li>{COMPANY_TRAINING_MODULES.length} Company Orientation Modules</li>
              <li>  - Executive Level (40 min)</li>
              <li>  - Management Level (50 min)</li>
              <li>  - Staff Level (30 min)</li>
            </ul>
          </div>

          <div>
            <h3 className="font-medium text-gray-900 mb-2">Policy Documents ({POLICY_DOCUMENTS.length} total)</h3>
            <ul className="list-disc list-inside text-gray-600 text-sm space-y-1 ml-4">
              <li>HIPAA Privacy Policy</li>
              <li>HIPAA Security Policy</li>
              <li>Breach Notification Procedures</li>
              <li>Business Associate Management Policy</li>
              <li>Disaster Recovery Plan</li>
              <li>Business Continuity Plan</li>
              <li>Security Incident Response Plan</li>
              <li>Access Control Policy</li>
              <li>Data Backup Procedures</li>
              <li>Vendor Management Policy</li>
              <li>Change Management Procedures</li>
              <li>Employee Handbook</li>
              <li>Code of Conduct</li>
              <li>Whistleblower Protection Policy</li>
              <li>Records Retention Policy</li>
              <li>Acceptable Use Policy</li>
              <li>Quality Assurance Program</li>
              <li>And more...</li>
            </ul>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-blue-800">
            <strong>Note:</strong> This will check for existing courses/documents and skip duplicates.
            All content will be stored in Firestore and accessible through the Training and Documents pages.
          </p>
        </div>

        <button
          onClick={handleSeedAll}
          disabled={seeding || !profile}
          className="w-full px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium"
        >
          {seeding ? 'Seeding...' : 'Seed All Training Courses & Documents'}
        </button>

        {seedResult && (
          <div
            className={`mt-6 p-4 rounded-lg border ${
              seedResult.success
                ? 'bg-green-50 border-green-200'
                : 'bg-yellow-50 border-yellow-200'
            }`}
          >
            <h3
              className={`font-semibold mb-2 ${
                seedResult.success ? 'text-green-800' : 'text-yellow-800'
              }`}
            >
              Seeding Complete
            </h3>
            <div className="space-y-2 text-gray-700">
              <p>
                <strong>Courses created:</strong> {seedResult.coursesCreated}
              </p>
              <p>
                <strong>Documents created:</strong> {seedResult.documentsCreated}
              </p>
              {seedResult.errors.length > 0 && (
                <div className="mt-4">
                  <p className="text-red-800 font-medium mb-2">Errors:</p>
                  <ul className="list-disc list-inside space-y-1 text-sm text-red-700">
                    {seedResult.errors.map((error, i) => (
                      <li key={i}>{error}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                You can now view the courses at <a href="/training" className="text-blue-600 hover:underline">/training</a> and 
                documents at <a href="/documents" className="text-blue-600 hover:underline">/documents</a>
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
